import { useEffect, useMemo, useRef, useState } from 'react';
import { avatarColor, initials } from '../mail/format';
import { suggest, type Suggestion } from '../mail/recents';
import { parseAddresses } from '../mail/quote';

/*
    A recipient field that shows what it understood.

    The old plain input took a comma-separated string and only told you it was
    wrong once you tried to send. Here each address becomes a chip the moment it
    is complete, so a typo is a chip that looks wrong rather than a message that
    won't go — and the raw text is still what gets stored, so nothing about the
    composer's plumbing had to change.
*/

export default function RecipientInput({
    value,
    onChange,
    directory,
    placeholder,
    disabled,
    testId,
}: {
    /** Comma-separated addresses — the same string the composer already keeps. */
    value: string;
    onChange: (next: string) => void;
    directory: Suggestion[];
    placeholder?: string;
    disabled?: boolean;
    testId?: string;
}) {
    const [draft, setDraft] = useState('');
    const [highlight, setHighlight] = useState(0);
    const [focused, setFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const chips = useMemo(() => {
        const parsed = parseAddresses(value);
        return [
            ...parsed.valid.map((a) => ({ label: a.name ? `${a.name} <${a.email}>` : a.email, email: a.email, bad: false })),
            ...parsed.invalid.map((raw) => ({ label: raw, email: raw, bad: true })),
        ];
    }, [value]);

    const matches = useMemo(() => {
        if (!draft.trim()) return [];
        const taken = new Set(chips.map((c) => c.email.toLowerCase()));
        return suggest(directory, draft).filter((s) => !taken.has(s.email.toLowerCase()));
    }, [draft, directory, chips]);

    useEffect(() => setHighlight(0), [draft]);

    const commit = (raw: string) => {
        const entry = raw.trim().replace(/[,;]+$/, '');
        if (!entry) return;
        const parts = value.split(',').map((s) => s.trim()).filter(Boolean);
        onChange([...parts, entry].join(', '));
        setDraft('');
    };

    const removeAt = (index: number) => {
        const parts = value.split(',').map((s) => s.trim()).filter(Boolean);
        parts.splice(index, 1);
        onChange(parts.join(', '));
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (matches.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault();
            setHighlight((h) => (h + (e.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length);
            return;
        }
        if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ';') {
            const pick = matches[highlight];
            if (draft.trim() || pick) {
                e.preventDefault();
                commit(pick ? (pick.name ? `${pick.name} <${pick.email}>` : pick.email) : draft);
            }
            return;
        }
        // Backspace on an empty field takes back the last chip, which is what
        // every chip field does and what fingers expect.
        if (e.key === 'Backspace' && !draft && chips.length) {
            e.preventDefault();
            removeAt(chips.length - 1);
        }
    };

    return (
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <div
                onClick={() => inputRef.current?.focus()}
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 5,
                    minHeight: 32,
                    padding: '3px 8px',
                    background: 'var(--surface2)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--r-btn)',
                    cursor: 'text',
                    opacity: disabled ? 0.6 : 1,
                }}
            >
                {chips.map((chip, i) => (
                    <span
                        key={`${chip.email}-${i}`}
                        title={chip.email}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            maxWidth: '100%',
                            padding: '2px 7px',
                            fontSize: 'var(--fs-xs)',
                            borderRadius: 'var(--r-pill)',
                            background: chip.bad ? 'var(--red-bg)' : 'var(--chip-bg)',
                            border: `1px solid ${chip.bad ? 'var(--red)' : 'var(--border)'}`,
                            color: chip.bad ? 'var(--red)' : 'var(--text)',
                        }}
                    >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{chip.label}</span>
                        <button
                            onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                            disabled={disabled}
                            style={{ background: 'transparent', border: 'none', color: 'inherit', padding: 0, lineHeight: 1 }}
                        >
                            ×
                        </button>
                    </span>
                ))}
                <input
                    ref={inputRef}
                    data-testid={testId}
                    value={draft}
                    disabled={disabled}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                    onFocus={() => setFocused(true)}
                    // Committed on blur, not discarded: a half-typed address that
                    // vanishes when you click Send is worse than a wrong chip.
                    onBlur={() => { setFocused(false); commit(draft); }}
                    placeholder={chips.length ? '' : placeholder}
                    style={{
                        flex: '1 0 120px',
                        minWidth: 80,
                        height: 24,
                        background: 'transparent',
                        border: 'none',
                        outline: 'none',
                        fontSize: 'var(--fs-md)',
                        color: 'var(--text)',
                    }}
                />
            </div>

            {focused && matches.length ? (
                <div
                    style={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: '100%',
                        marginTop: 4,
                        zIndex: 70,
                        padding: 4,
                        background: 'var(--surface)',
                        border: '1px solid var(--border2)',
                        borderRadius: 'var(--r-card)',
                        boxShadow: '0 14px 34px rgba(0,0,0,0.35)',
                        maxHeight: 240,
                        overflowY: 'auto',
                    }}
                >
                    {matches.map((match, i) => (
                        <button
                            key={match.email}
                            // mousedown, not click: blur fires first and would
                            // commit the half-typed text before the click lands.
                            onMouseDown={(e) => {
                                e.preventDefault();
                                commit(match.name ? `${match.name} <${match.email}>` : match.email);
                            }}
                            onMouseEnter={() => setHighlight(i)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 9,
                                width: '100%',
                                padding: '6px 8px',
                                textAlign: 'left',
                                border: 'none',
                                borderRadius: 7,
                                background: i === highlight ? 'var(--accent-bg)' : 'transparent',
                                color: 'var(--text)',
                            }}
                        >
                            <span
                                style={{
                                    width: 24,
                                    height: 24,
                                    flex: '0 0 auto',
                                    borderRadius: 'var(--r-pill)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 'var(--fs-2xs)',
                                    fontWeight: 700,
                                    color: '#fff',
                                    overflow: 'hidden',
                                    background: avatarColor(match.email),
                                }}
                            >
                                {initials(match.name || match.email)}
                            </span>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'block', fontSize: 'var(--fs-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {match.name || match.email}
                                </span>
                                {match.name ? (
                                    <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)', color: 'var(--text-mute)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {match.email}
                                    </span>
                                ) : null}
                            </span>
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
